import { useStore } from '../../store/useStore';
import { WindowManager } from '../WindowManager/WindowManager';
import { Taskbar } from '../Taskbar/Taskbar';
import { DesktopIcon } from './DesktopIcon';
import { wallpaperStyle } from './wallpapers';
import { TerminalSquare, Folder, Clipboard, Settings, MessageSquarePlus, Globe, Calculator } from 'lucide-react';
import type { AppType } from '../../types';

interface DesktopEntry {
  appType: AppType;
  label: string;
  icon: React.ReactNode;
}

export function Desktop() {
  const windows = useStore((s) => s.windows);
  const openApp = useStore((s) => s.openApp);
  const theme = useStore((s) => s.theme);
  const currentProject = useStore((s) => s.currentProject);

  const icons: DesktopEntry[] = [
    { appType: 'file-manager', label: currentProject ? currentProject.name : 'Files', icon: <Folder className="w-9 h-9 text-amber-400" /> },
    { appType: 'terminal', label: 'Terminal', icon: <TerminalSquare className="w-9 h-9 text-cyan-400" /> },
    { appType: 'projects', label: 'Projects', icon: <Clipboard className="w-9 h-9 text-purple-400" /> },
    { appType: 'calculator', label: 'Calculator', icon: <Calculator className="w-9 h-9 text-orange-400" /> },
    { appType: 'chat', label: 'AI Assistant', icon: <MessageSquarePlus className="w-9 h-9 text-blue-400" /> },
    { appType: 'browser', label: 'Browser', icon: <Globe className="w-9 h-9 text-pink-400" /> },
    { appType: 'settings', label: 'Settings', icon: <Settings className="w-9 h-9 text-slate-400" /> },
  ];

  const settings = useStore((s) => s.settings);
  const customWallpaper = wallpaperStyle(settings.wallpaper);

  return (
    <div
      className={`h-full w-full relative flex flex-col ${theme === 'dark' ? 'dark' : 'light'}`}
    >
      <div
        className={`flex-1 relative overflow-hidden ${
          customWallpaper
            ? ''
            : theme === 'dark'
              ? 'bg-gradient-to-br from-[#1a1b26] via-[#141863] to-[#1a1b26]'
              : 'bg-gradient-to-br from-blue-100 via-indigo-50 to-purple-100'
        }`}
        style={customWallpaper}
      >
        {/* Subtle grid pattern overlay for dark mode */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              theme === 'dark'
                ? 'radial-gradient(circle at 25% 25%, rgba(122,162,247,0.06) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(187,154,247,0.06) 0%, transparent 50%)'
                : 'radial-gradient(circle at 25% 25%, rgba(122,162,247,0.12) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(187,154,247,0.12) 0%, transparent 50%)',
          }}
        />

        {/* Desktop icons on the left (below windows, like Windows) */}
        <div className="absolute top-4 left-4 z-0 flex flex-col gap-1">
          {icons.map((icon) => (
            <DesktopIcon key={icon.label} icon={icon.icon} label={icon.label} onOpen={() => openApp(icon.appType)} />
          ))}
        </div>

        {/* Window manager fills the desktop (clicks pass through empty areas to icons) */}
        <div className="absolute inset-0 z-10 pointer-events-none">
          <WindowManager windows={windows} />
        </div>
      </div>

      <Taskbar />
    </div>
  );
}